import {
  Flex,
  Heading,
  ToggleGroup,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';

import { MobileTariff, MobileTariffType } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';

import { MobilePromoNote } from '../../MobilePromoNote';
import { SimSettingsFormValues } from '../../types';
import { SimPlanTile } from './SimPlanTile';
import { SimPlanTileSkeleton } from './SimPlanTile/Skeleton';
import './styles.scss';

interface ChooseSimFormSectionProps {
  mobileTariffs: MobileTariff[];
  showUnlimitedMaxPromoLegal: boolean;
  loading: boolean;
}

const HEADING_ID = 'choose-sim-heading';

const ChooseSimFormSection = ({
  mobileTariffs,
  showUnlimitedMaxPromoLegal,
  loading = false,
}: ChooseSimFormSectionProps) => {
  const essential = mobileTariffs.find(
    (t) => t.tariffType === MobileTariffType.EssentialMax
  );
  const goEssential = mobileTariffs.find(
    (t) => t.tariffType === MobileTariffType.GoEssential
  );
  const unlimited = mobileTariffs.find(
    (t) => t.tariffType === MobileTariffType.UnlimitedMax
  );
  const goUnlimited = mobileTariffs.find(
    (t) => t.tariffType === MobileTariffType.GoUnlimited
  );
  const showEuRoamingNote = Boolean(
    essential || goEssential || unlimited || goUnlimited
  );

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    submitCount,
    setFieldValue,
  } = useFormikContext<SimSettingsFormValues>();

  const getFieldProps = useFieldProps<SimSettingsFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <SimPlanTileSkeleton />
          <SimPlanTileSkeleton />
        </>
      );
    }

    return (
      <>
        {essential && <SimPlanTile tariff={essential} />}
        {goEssential && <SimPlanTile tariff={goEssential} />}
        {unlimited && <SimPlanTile tariff={unlimited} />}
        {goUnlimited && <SimPlanTile tariff={goUnlimited} />}
      </>
    );
  };

  return (
    <Flex asChild direction="column" spacing="lg">
      <section className="chooseSimFormSection__wrapper">
        <Heading
          id={HEADING_ID}
          as="h2"
          size="lg"
          data-testid="mobile-sim-form-title"
        >
          Choose your SIM plan
        </Heading>
        {errors.simPlanId && touched.simPlanId && (
          <ValidationText status="invalid">
            Please choose a SIM plan
          </ValidationText>
        )}
        <ToggleGroup
          type="single"
          aria-labelledby={HEADING_ID}
          {...getFieldProps('simPlanId')}
          value={values.simPlanId ?? undefined}
          onValueChange={(value: string) => setFieldValue('simPlanId', value)}
          spacing="xl"
          paddingTop="300"
          direction={{
            tablet: 'row',
            mobile: 'column',
          }}
        >
          {renderContent()}
        </ToggleGroup>
        {(showUnlimitedMaxPromoLegal || showEuRoamingNote) && (
          <MobilePromoNote
            showTransferPromoText={showUnlimitedMaxPromoLegal}
            showEuRoamingText={showEuRoamingNote}
          />
        )}
      </section>
    </Flex>
  );
};

export { ChooseSimFormSection };
